/**
 * Programme actif (contexte GLOBAL) — le niveau au-dessus du projet. Calqué sur
 * useWorkspace, à une différence près : « aucun programme » (null) est un état VALIDE
 * et par défaut (= tous les projets du locataire) ; on ne retombe donc jamais
 * automatiquement sur le premier programme. Persisté pour survivre au rechargement.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
  type JSX, type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'

import { api, type Program } from '../api/client'

const STORAGE_KEY = 'doit-active-program'

function lireStocke(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function ecrireStocke(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* stockage indisponible : la sélection reste valable en mémoire pour la session */
  }
}

interface ProgramContextValue {
  programs: Program[]
  isLoading: boolean
  error: Error | null
  activeId: string | null
  active: Program | null
  setActiveId: (id: string | null) => void
}

const ProgramContext = createContext<ProgramContextValue | null>(null)

export function ProgramProvider({ children }: { children: ReactNode }): JSX.Element {
  const [activeId, setActiveIdState] = useState<string | null>(() => lireStocke())
  const query = useQuery({ queryKey: ['programs'], queryFn: api.listPrograms })

  const setActiveId = useCallback((id: string | null) => {
    setActiveIdState(id)
    ecrireStocke(id)
  }, [])

  const value = useMemo<ProgramContextValue>(() => {
    const programs = query.data ?? []
    // On n'honore la sélection persistée que si le programme existe encore et appartient
    // au locataire. Sinon → null (tous les projets), PAS le premier programme.
    const connu = activeId && programs.some((p) => p.program_id === activeId) ? activeId : null
    return {
      programs,
      isLoading: query.isPending,
      error: (query.error as Error | null) ?? null,
      activeId: connu,
      active: programs.find((p) => p.program_id === connu) ?? null,
      setActiveId,
    }
  }, [query.data, query.isPending, query.error, activeId, setActiveId])

  return <ProgramContext.Provider value={value}>{children}</ProgramContext.Provider>
}

export function useProgram(): ProgramContextValue {
  const context = useContext(ProgramContext)
  if (!context) throw new Error('useProgram doit être utilisé dans un ProgramProvider')
  return context
}

/** Variante sans exigence de fournisseur (`useChangerContexte` hors `ProgramProvider`, tests d'écran). */
export function useProgramOptionnel(): ProgramContextValue | null {
  return useContext(ProgramContext)
}
